import React from 'react';

import { DomainNameApprovalStatus, DomainNameVerificationStatus } from '@dashboard/generated/wp/platform';
import { CheckCircleOutline, ErrorOutline, HelpOutlineOutlined, RemoveCircleOutline } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';

type TStatus = 'SUCCEEDED' | 'FAILED' | 'PENDING' | 'PENDING_2' | 'UNKNOWN';
type Props = {
  status: TStatus;
  label: string;
};

const renderColor = (status: TStatus) => {
  switch (status) {
    case 'SUCCEEDED':
      return 'brand.green';
    case 'FAILED':
      return 'brand.red';
    case 'PENDING_2':
    case 'PENDING':
    case 'UNKNOWN':
      return 'grey.600';
  }
};

const renderIcon = (status: TStatus) => {
  switch (status) {
    case 'SUCCEEDED':
      return <CheckCircleOutline fontSize="small" />;
    case 'FAILED':
      return <ErrorOutline fontSize="small" />;
    case 'PENDING':
    case 'PENDING_2':
      return <RemoveCircleOutline fontSize="small" />;
    case 'UNKNOWN':
      return <HelpOutlineOutlined fontSize="small" />;
  }
};

function Status({ status, label }: Props) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ color: renderColor(status) }}>
      {renderIcon(status)}
      <Typography>{label}</Typography>
    </Stack>
  );
}

export const renderDomainNameApprovalStatus = (status: DomainNameApprovalStatus) => {
  switch (status) {
    case DomainNameApprovalStatus.Pending:
      return <Status status="PENDING_2" label="Awaiting DNS verification" />;
    case DomainNameApprovalStatus.Requested:
      return <Status status="PENDING" label="Pending" />;
    case DomainNameApprovalStatus.Approved:
      return <Status status="SUCCEEDED" label="Approved" />;
    case DomainNameApprovalStatus.Rejected:
      return <Status status="FAILED" label="Rejected" />;
  }
};

export const renderDomainNameVerificationStatus = (status: DomainNameVerificationStatus) => {
  switch (status) {
    case 'SUCCEEDED':
      return <Status status="SUCCEEDED" label="Verified" />;
    case 'FAILED':
      return <Status status="FAILED" label="Failed" />;
    case 'PENDING':
      return <Status status="PENDING" label="Pending" />;
    default:
      return <Status status="UNKNOWN" label="Unknown" />;
  }
};
